import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Target, TrendingDown, Layers, CalendarClock, Wallet, Trophy,
  Mail, Building2, Phone, Globe, Eye, MousePointerClick, CalendarDays,
} from 'lucide-react';

const STATUS_STYLES = {
  active: 'bg-brand-blue/10 text-brand-blue border-brand-blue/20',
  booked: 'bg-green-500/10 text-green-400 border-green-500/20',
  completed: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  unsubscribed: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const ANSWER_FIELDS = [
  { key: 'objective', label: 'Strategic Objective', icon: Target },
  { key: 'costOfInaction', label: 'Cost of Inaction (6 months)', icon: TrendingDown },
  { key: 'infrastructure', label: 'Infrastructure', icon: Layers },
  { key: 'timeline', label: 'Target Timeline', icon: CalendarClock },
  { key: 'budget', label: 'Budget Allocation', icon: Wallet },
  { key: 'kpi', label: '12-Month KPI', icon: Trophy },
];

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleString(); } catch (e) { return '\u2014'; }
};

export const LeadDetailDrawer = ({ lead, onClose }) => {
  const open = Boolean(lead);
  const app = (lead && lead.application) || {};
  const drip = (lead && lead.drip) || {};
  const status = (drip.status || 'none');
  const badge = STATUS_STYLES[status] || 'bg-slate-700 text-slate-300 border-slate-600';

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[200] flex justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="lead-detail-drawer">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="lead-drawer-backdrop" />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md h-full bg-slate-900 border-l border-slate-800 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="shrink-0 flex items-start justify-between px-5 py-4 border-b border-slate-800">
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Executive Application</p>
                <h2 className="text-lg font-bold text-white truncate" data-testid="lead-drawer-name">{lead?.name || 'Unnamed Lead'}</h2>
                <p className="text-xs text-slate-400 truncate" data-testid="lead-drawer-email">{lead?.email || '\u2014'}</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-800 transition-colors shrink-0" data-testid="lead-drawer-close"><X className="w-4 h-4 text-slate-500" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              {/* Drip status + engagement */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-slate-400">Drip Status</span>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${badge}`} data-testid="lead-drawer-status">{status.toUpperCase()}</span>
                </div>
                <div className="grid grid-cols-3 gap-2.5">
                  <div className="bg-slate-800 rounded-xl border border-slate-700 p-3 text-center" data-testid="lead-drawer-day">
                    <CalendarDays className="w-4 h-4 text-brand-blue mx-auto mb-1" />
                    <p className="text-lg font-bold text-white">{drip.day_index || 0}<span className="text-xs text-slate-500">/7</span></p>
                    <p className="text-[10px] text-slate-500">Day Index</p>
                  </div>
                  <div className="bg-slate-800 rounded-xl border border-slate-700 p-3 text-center" data-testid="lead-drawer-opens">
                    <Eye className="w-4 h-4 text-brand-orange mx-auto mb-1" />
                    <p className="text-lg font-bold text-white">{drip.opens_total || 0}</p>
                    <p className="text-[10px] text-slate-500">Opens</p>
                  </div>
                  <div className="bg-slate-800 rounded-xl border border-slate-700 p-3 text-center" data-testid="lead-drawer-clicks">
                    <MousePointerClick className="w-4 h-4 text-brand-orange mx-auto mb-1" />
                    <p className="text-lg font-bold text-white">{drip.clicks_total || 0}</p>
                    <p className="text-[10px] text-slate-500">Clicks</p>
                  </div>
                </div>
                {drip.last_sent_at && <p className="text-[10px] text-slate-500 mt-2">Last email sent: {fmtDate(drip.last_sent_at)}</p>}
              </div>

              {/* Contact meta */}
              <div className="space-y-2">
                {lead?.company && <div className="flex items-center gap-2 text-xs text-slate-300"><Building2 className="w-3.5 h-3.5 text-slate-500" /> {lead.company}</div>}
                {lead?.phone && <div className="flex items-center gap-2 text-xs text-slate-300"><Phone className="w-3.5 h-3.5 text-slate-500" /> {lead.phone}</div>}
                <div className="flex items-center gap-2 text-xs text-slate-300"><Mail className="w-3.5 h-3.5 text-slate-500" /> {lead?.email || '\u2014'}</div>
                {lead?.timezone && <div className="flex items-center gap-2 text-xs text-slate-300"><Globe className="w-3.5 h-3.5 text-slate-500" /> {lead.timezone}</div>}
                <p className="text-[10px] text-slate-500 pt-1">Applied {fmtDate(lead?.created_at)}</p>
              </div>

              {/* Application answers */}
              <div>
                <p className="text-xs font-semibold text-slate-400 mb-3">Application Answers</p>
                <div className="space-y-3">
                  {ANSWER_FIELDS.map(({ key, label, icon: Icon }) => (
                    <div key={key} className="bg-slate-800 rounded-xl border border-slate-700 p-4" data-testid={`lead-drawer-answer-${key}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="w-3.5 h-3.5 text-brand-blue" />
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">{label}</span>
                      </div>
                      <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap break-words">{app[key] ? app[key] : <span className="text-slate-600">Not answered</span>}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
